import { supabase } from '../lib/supabase'
import type { WrapUpPhoto } from './wrapUpPhotos'

/**
 * Uploading the report's photographs. Staff side only.
 *
 * Same shape as the call sheets (`utils/callSheetStore.ts`) and the shot
 * references: a public bucket, a per-project folder, a single write path so
 * nothing is reported as saved until it genuinely is.
 *
 * WHY THE BROWSER RESIZES BEFORE UPLOADING. A delivered frame off a camera is
 * 5500px wide and several megabytes; eight of them is a sixty-megabyte page
 * for a client who is reading a report on a phone at an airport. Nothing in
 * this report is ever shown wider than about a thousand CSS pixels, so the
 * long edge is capped at 2000 -- enough to stay sharp on a retina screen, and
 * roughly a twentieth of the bytes. The aspect ratio is untouched, so the
 * portrait-or-landscape decision the layout makes is unaffected.
 *
 * The originals are not Portal's to keep: the client's real photographs live
 * in the photography deliverable's own Drive folder, which is what the
 * downloads section at the end of the report points at.
 */

const BUCKET = 'wrap-up-photos'
const MAX_EDGE = 2000
const QUALITY = 0.82
/** Before resizing. A frame bigger than this is a raw file, not a photograph. */
const MAX_SOURCE_BYTES = 40 * 1024 * 1024

function id(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

interface Resized {
  blob: Blob
  width: number
  height: number
}

/** Draws the file into a canvas at a sane size and hands back a JPEG. */
async function resize(file: File): Promise<Resized> {
  const source = await new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('That file could not be read as an image.'))
    }
    image.src = url
  })

  const longest = Math.max(source.naturalWidth, source.naturalHeight)
  const scale = longest > MAX_EDGE ? MAX_EDGE / longest : 1
  const width = Math.round(source.naturalWidth * scale)
  const height = Math.round(source.naturalHeight * scale)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('This browser could not prepare the image.')
  context.drawImage(source, 0, 0, width, height)

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', QUALITY),
  )
  if (!blob) throw new Error('This browser could not prepare the image.')

  return { blob, width, height }
}

export interface PhotoUpload {
  ok: boolean
  photo?: WrapUpPhoto
  error?: string
}

/** Resizes and stores one photograph. */
export async function uploadWrapUpPhoto(projectId: string, file: File): Promise<PhotoUpload> {
  if (!supabase) return { ok: false, error: 'No database connection.' }
  if (!projectId) return { ok: false, error: 'Save the project first.' }
  if (!file.type.startsWith('image/')) {
    return { ok: false, error: `${file.name} is not an image.` }
  }
  if (file.size > MAX_SOURCE_BYTES) {
    return { ok: false, error: `${file.name} is too large — export it smaller than 40MB.` }
  }

  let prepared: Resized
  try {
    prepared = await resize(file)
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'Could not read that image.' }
  }

  const path = `${projectId}/${id()}.jpg`
  const { error } = await supabase.storage.from(BUCKET).upload(path, prepared.blob, {
    contentType: 'image/jpeg',
    upsert: false,
  })

  if (error) {
    console.warn('[supabase] report photo upload failed:', error.message)
    return { ok: false, error: `${file.name} could not be saved. Nothing was stored.` }
  }

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path)
  return {
    ok: true,
    photo: {
      path,
      url: data.publicUrl,
      width: prepared.width,
      height: prepared.height,
    },
  }
}

/** Best-effort cleanup when staff take a photograph out of the report. */
export async function removeWrapUpPhoto(path: string | undefined): Promise<void> {
  if (!supabase || !path) return
  const { error } = await supabase.storage.from(BUCKET).remove([path])
  if (error) console.warn('[supabase] report photo removal failed:', error.message)
}
